import { Button, ButtonType, SubmitButton, Switch, FormGroup, Input, InputType, FormFieldSize, StandardModal, Select, TextArea, ModalTypes, Tooltip, Spinner, SpinnerSizeTypes } from '@digicert/dcone-common-ui'
import React, { useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import styles from './SCIMProvisioning.module.scss'
import { connect } from 'react-redux'
import { translate } from 'shared/helpers/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCopy, faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { getSCIMConfig, updateSCIMConfig, generateSCIMToken } from './SCIMProvisioning.actions'
import { ISCIMConfigPayload, ISCIMConfigUpdatePayload, ISCIMProvisioningProps, ISCIMGenerateTokenPayload, ISCIMConfig } from './SCIMProvisioning.types'

const expirationOptions = [
  { label: '90 days', value: '90' },
  { label: '180 days', value: '180' },
  { label: '1 year', value: '365' },
  { label: '2 years', value: '730' },
  { label: '5 years', value: '1825' }
]


const SCIMProvisioning = ({ onCancel, accountId, updateSCIMConfig, generateSCIMToken, scimConfig, setScimConfig, getSCIMConfig }: ISCIMProvisioningProps) => {
  const [enableSCIM, setEnableSCIM] = useState(false)
  const [copied, setCopied] = useState(false)
  const [expirationDays, setExpirationDays] = useState('365')
  const [tokenCopied, setTokenCopied] = useState(false)
  const [token, setToken] = useState('')
  const [showGenerateTokenModal, setShowGenerateTokenModal] = useState(false)
  const [isGeneratingToken, setIsGeneratingToken] = useState(false)

  const resetData = () => {
    setEnableSCIM(scimConfig?.status === 'ENABLED')
    setCopied(false)
    setExpirationDays('365')
    setTokenCopied(false)
    setToken('')
    setShowGenerateTokenModal(false)
    setScimConfig(scimConfig || null)
  }

  useEffect(() => {
    resetData()
  }, [onCancel])


  useEffect(() => {
    if (scimConfig) {
      setEnableSCIM(scimConfig.status === 'ENABLED')
    }
  }, [scimConfig])


  const generateToken = () => {
    setIsGeneratingToken(true)
    const payload: ISCIMGenerateTokenPayload = {
      id: scimConfig?.id || null,
      validity_days: parseInt(expirationDays, 10),
      status: 'ENABLED',
      regenerate_token: false
    }
    generateSCIMToken(payload, <Translate id='accounts.details.scim.tokenGenerated' />).then((response: ISCIMConfigPayload) => {
      setToken(response?.token)
    })
      .finally(() => {
        setIsGeneratingToken(false)
      })
  }

  const regenerateToken = () => {
    setIsGeneratingToken(true)
    const payload: ISCIMGenerateTokenPayload = {
      id: scimConfig?.id || null,
      validity_days: parseInt(expirationDays, 10),
      status: 'ENABLED',
      regenerate_token: true
    }
    generateSCIMToken(payload, <Translate id='accounts.details.scim.tokenGenerated' />).then((response: ISCIMConfigPayload) => {
      setToken(response.token)
    })
      .finally(() => {
        setIsGeneratingToken(false)
      })
  }

  const updateExpirationDays = (value: any) => {
    setExpirationDays(value)
  }

  const copyToClipboard = (textToCopy): void => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1000)
    });
  }

  const copyTokenClipboard = (textToCopy): void => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setTokenCopied(true)
    })
  }

  const handleToggleSCIM = (checked: boolean) => {
    setEnableSCIM(checked)
  }

  const saveConfig = () => {
    const payload: ISCIMConfigUpdatePayload = {
      status: enableSCIM ? 'ENABLED' : 'DISABLED',
    }
    updateSCIMConfig(scimConfig?.id, payload, <Translate id='accounts.details.scim.configurationUpdated' />)
      .then(() => {
        getSCIMConfig(accountId).then((res: ISCIMConfig) => {
          setScimConfig(res)
          setEnableSCIM(res?.status === 'ENABLED')
        })
      })
    if (onCancel) {
      onCancel()
    }
  }

  const handleOkCallback = () => {
    if ((scimConfig?.status === 'ENABLED' || scimConfig?.status === 'DISABLED') && !token) {
      regenerateToken()
    }
    else if (scimConfig?.status === 'INITIAL' && !token) {
      generateToken()
    }
    else if (token) {
      setShowGenerateTokenModal(false)
      getSCIMConfig(accountId).then((response: ISCIMConfig) => {
        setScimConfig(response)
        setEnableSCIM(response?.status === 'ENABLED')
        if (onCancel) {
          onCancel()
        }
      })
    }
  }

  const handleCloseBtn = () => {
    if (token) {
      getSCIMConfig(accountId).then((response: ISCIMConfig) => {
        setScimConfig(response)
        setEnableSCIM(response?.status === 'ENABLED')
      })
      if (onCancel) {
        onCancel()
      }
    }
    setShowGenerateTokenModal(false)
  }

  return (
    <>
      <section className={styles.scimProvisioning}>
        <div className={styles.toggleSCIM}>
          <h6 className={styles.heading}><Translate id='accounts.details.scim.enableSync' /></h6>
          <p className={styles.description}><Translate id='accounts.details.scim.syncEnabledDesc' /> <a href="https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/automated-user-provisioning-with-scim/enable-scim.html" target="_blank" rel="noopener noreferrer"><Translate id='accounts.details.scim.learnMore' /></a></p>
          <Switch
            offLabel={translate('common.form.buttons.off') as string}
            onChange={handleToggleSCIM}
            onLabel={translate('common.form.buttons.on') as string}
            size='default'
            checked={enableSCIM}
          />
        </div>
        <Input
          size={FormFieldSize.XXL}
          type={InputType.TEXT}
          name='url'
          label={<Translate id='accounts.details.scim.baseUrl' />}
          help={<Translate id='accounts.details.scim.copyUrlDescription' />}
          value={scimConfig?.scim_url || ''}
          readOnly
        />
        {copied && (
          <div className={styles.copiedContainer} onClick={() => copyToClipboard(scimConfig?.scim_url)}>
            <FontAwesomeIcon size='lg' icon={faCheck} className={styles.clipBoardIcon} />
            <p>{translate('accounts.details.scim.copied') as string}</p>
          </div>
        )}
        {!copied && (
          <div className={styles.copyContainer} onClick={() => copyToClipboard(scimConfig?.scim_url)}>
            <FontAwesomeIcon size='lg' icon={faCopy} className={styles.clipBoardIcon}></FontAwesomeIcon>
            <p>{translate('accounts.details.scim.copy') as string}</p>
          </div>
        )}
        <div className={styles.formActions}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
            <Translate id='accounts.details.scim.close' />
          </Button>
          {
            (scimConfig?.status !== 'INITIAL') ? (<>
              <Button buttonType={ButtonType.SECONDARY} disabled={!enableSCIM} onClick={() => setShowGenerateTokenModal(true)} rightIcon={<Tooltip title={<Translate id='accounts.details.scim.tokenNote' />}><FontAwesomeIcon icon={faInfoCircle} /></Tooltip>}>
                <Translate id='accounts.details.scim.regenerateToken' />
              </Button>
              <SubmitButton buttonType={ButtonType.PRIMARY} onClick={saveConfig}>
                <Translate id='accounts.details.scim.save' />
              </SubmitButton>
            </>
            ) :
              <SubmitButton disabled={!enableSCIM} buttonType={ButtonType.PRIMARY} onClick={() => setShowGenerateTokenModal(true)}>
                <Translate id='accounts.details.scim.generateToken' />
              </SubmitButton>
          }
        </div>
      </section>
      <StandardModal
        className={styles.generateTokenModal}
        modalType={ModalTypes.STANDARD}
        headline={<Translate id='accounts.details.scim.generateToken' />}
        display={showGenerateTokenModal}
        buttonLabel={!token ? <div className={styles.buttonLabel}>
          <Translate id='accounts.details.scim.generateToken' />
          {isGeneratingToken && <Spinner size={SpinnerSizeTypes.SMALL} />} </div> : <Translate id='common.form.buttons.doneBtn' />}
        onOkCallBack={handleOkCallback}
        hideCancelButton={true}
        onCancelCallBack={handleCloseBtn}
        buttonCancelLabel={null}
        disableButton={(!tokenCopied && !!token) || isGeneratingToken}
        maskClosable={false}
      >
        <FormGroup>
          <Select
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            size={FormFieldSize.XXL}
            allowClear
            defaultValue={expirationDays}
            value={expirationDays}
            label={translate('accounts.details.scim.tokenExpiration') as string}
            help={translate("accounts.details.scim.tokenExpirationNote") as string}
            onChange={updateExpirationDays}
            options={expirationOptions}
            errors={{ valueMissing: translate('common.form.validation.required') as string }}
            optionFilterProp="label"
            showSearch
            disabled={!!token}
          />
        </FormGroup>
        {token && <>
          <TextArea
            size={FormFieldSize.XXL}
            value={token}
            label={translate('accounts.details.scim.token') as string}
            readOnly
            help={translate('accounts.details.scim.tokenSecurityNote') as string}
          />
          {tokenCopied && (
            <div className={styles.copiedContainer}>
              <FontAwesomeIcon size='lg' icon={faCheck} className={styles.clipBoardIcon} /><p>{translate('accounts.details.scim.copied') as string}</p>
            </div>

          )
          }
          {!tokenCopied && (
            <div className={styles.copyContainer} onClick={() => copyTokenClipboard(token)}>
              <FontAwesomeIcon size='lg' icon={faCopy} className={styles.clipBoardIcon}></FontAwesomeIcon><p>{translate('accounts.details.scim.copy') as string}</p>
            </div>

          )
          }
        </>}
      </StandardModal>
    </>
  )
}

export default connect(null, { getSCIMConfig, updateSCIMConfig, generateSCIMToken })(SCIMProvisioning)
